import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, X, FileDown, Save, Send, Phone as PhoneIcon, MessageSquare, Video, Users, Mail, StickyNote, MoreHorizontal, Activity, FileText, Calendar, RefreshCw, Maximize2, Minimize2, GripVertical, Copy, BookOpen, PenSquare } from "lucide-react";
import ProposalSendModal from "@/components/admin/ProposalSendModal";

const emptyItem = (preset = {}) => ({ service_id: null, name: "", description: "", qty: 1, unit_price: 0, vat_rate: 20, is_recurring: false, billing_cycle: "month", ...preset });

const EXCHANGE_KINDS = [
  { v: "appel", l: "Appel téléphonique", icon: PhoneIcon, color: "#06B6D4" },
  { v: "rdv_physique", l: "RDV physique", icon: Users, color: "#FFDCA8" },
  { v: "visio", l: "Visio", icon: Video, color: "#A7F3D0" },
  { v: "sms", l: "SMS", icon: MessageSquare, color: "#67E8F9" },
  { v: "email", l: "Email", icon: Mail, color: "#94a3b8" },
  { v: "note", l: "Note interne", icon: StickyNote, color: "#FFDCA8" },
  { v: "autre", l: "Autre", icon: MoreHorizontal, color: "#cbd5e1" },
];

export default function ProposalEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "nouveau";

  const [services, setServices] = useState([]);
  const [data, setData] = useState({
    client: { name: "", email: "", phone: "", company: "", address: "" },
    project: { title: "", brief: "", objectives: [], deadline: "", notes: "" },
    items: [],
    payment_terms: "50_50",
    installment_count: 6,
    installment_start: "",
    payment_mode: "manual_link",
    notes: "",
    status: "brouillon",
    probability: 10,
    discount_kind: null,    // "percent" | "amount" | null
    discount_value: 0,
    discount_label: "",
  });
  const [loaded, setLoaded] = useState(isNew);
  const [propId, setPropId] = useState(isNew ? null : id);
  const [propRef, setPropRef] = useState("");
  const [token, setToken] = useState("");
  const [sendModal, setSendModal] = useState(false);
  const [exchanges, setExchanges] = useState([]);
  const [newExchange, setNewExchange] = useState({ kind: "appel", note: "" });
  const [exBusy, setExBusy] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);  // only for new proposals — emails the client a welcome/password-setup link if their account is auto-created

  const loadExchanges = useCallback(() => {
    if (!propId || isNew) return;
    api.get(`/admin/proposals/${propId}/exchanges`).then((r) => setExchanges(r.data || [])).catch(() => {});
  }, [propId, isNew]);

  useEffect(() => { loadExchanges(); }, [loadExchanges]);

  useEffect(() => {
    api.get("/admin/services").then((r) => setServices((r.data || []).filter((s) => s.active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/proposals/${id}`).then((r) => {
      const c = r.data.client || {};
      // Backward-compat: derive first_name/last_name from "name" if not stored separately
      if (!c.first_name && !c.last_name && c.name) {
        const parts = String(c.name).trim().split(/\s+/);
        c.first_name = parts[0] || "";
        c.last_name = parts.length > 1 ? parts.slice(1).join(" ") : "";
      }
      setData({
        client: c,
        project: { ...r.data.project, objectives: r.data.project?.objectives || [] },
        items: r.data.items || [],
        payment_terms: r.data.payment_terms || "50_50",
        installment_count: r.data.installment_count || 6,
        installment_start: r.data.installment_start || "",
        payment_mode: r.data.payment_mode || "manual_link",
        notes: r.data.notes || "",
        status: r.data.status || "brouillon",
        probability: typeof r.data.probability === "number" ? r.data.probability : 10,
        discount_kind: r.data.discount_kind || null,
        discount_value: r.data.discount_value || 0,
        discount_label: r.data.discount_label || "",
      });
      setPropRef(r.data.ref || "");
      setToken(r.data.public_token || "");
      setLoaded(true);
    }).catch(() => toast.error("Devis introuvable"));
  }, [id, isNew]);

  const totals = useMemo(() => {
    let ht = 0, vat = 0, rmHt = 0, rmVat = 0, ryHt = 0, ryVat = 0;
    for (const it of data.items) {
      const qty = parseFloat(it.qty) || 0;
      const pu = parseFloat(it.unit_price) || 0;
      const gross = qty * pu;
      // Per-line discount
      const ldk = it.discount_kind;
      const ldv = parseFloat(it.discount_value) || 0;
      let lineDisc = 0;
      if (ldk === "percent" && ldv > 0) lineDisc = Math.round(gross * Math.min(100, ldv) / 100 * 100) / 100;
      else if (ldk === "amount" && ldv > 0) lineDisc = Math.round(Math.min(ldv, gross) * 100) / 100;
      const netHt = gross - lineDisc;
      const rate = parseFloat(it.vat_rate) || 0;
      const netVat = netHt * rate / 100;
      if (it.is_recurring) {
        if ((it.billing_cycle || "month") === "month") { rmHt += netHt; rmVat += netVat; }
        else { ryHt += netHt; ryVat += netVat; }
      } else { ht += netHt; vat += netVat; }
    }
    // Global commercial discount on HT subtotal (one-off only).
    const dv = parseFloat(data.discount_value) || 0;
    let discount_ht = 0;
    if (data.discount_kind === "percent" && dv > 0) discount_ht = Math.round(ht * Math.min(100, dv) / 100 * 100) / 100;
    else if (data.discount_kind === "amount" && dv > 0) discount_ht = Math.round(Math.min(dv, ht) * 100) / 100;
    const ratio = ht > 0 ? (1 - discount_ht / ht) : 1;
    const vat_after = Math.round(vat * ratio * 100) / 100;
    const ht_after = Math.round((ht - discount_ht) * 100) / 100;
    return {
      subtotal_ht: ht,
      discount_amount_ht: discount_ht,
      subtotal_after_discount_ht: ht_after,
      vat: vat_after,
      total_ttc: Math.round((ht_after + vat_after) * 100) / 100,
      recurring_monthly_ht: rmHt,
      recurring_monthly_ttc: Math.round((rmHt + rmVat) * 100) / 100,
      recurring_yearly_ht: ryHt,
      recurring_yearly_ttc: Math.round((ryHt + ryVat) * 100) / 100,
    };
  }, [data.items, data.discount_kind, data.discount_value]);

  const addItem = (preset = {}) => setData((d) => ({ ...d, items: [...d.items, emptyItem(preset)] }));

  const duplicateItem = (i) => setData((d) => {
    const it = d.items[i];
    const copy = { ...it };
    return { ...d, items: [...d.items.slice(0, i + 1), copy, ...d.items.slice(i + 1)] };
  });

  const saveItemToCatalog = async (i) => {
    const it = data.items[i];
    if (!it.name || !it.unit_price) { toast.error("Renseigne au moins le nom et le prix"); return; }
    try {
      await api.post("/admin/services", {
        name: it.name,
        description: it.description || "",
        price_eur: parseFloat(it.unit_price) || 0,
        vat_rate: parseFloat(it.vat_rate) || 20,
        is_recurring: !!it.is_recurring,
        billing_cycle: it.billing_cycle || "month",
        category: "Personnalisé",
      });
      toast.success(`"${it.name}" enregistré dans le catalogue`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const moveItem = (from, to) => setData((d) => {
    if (from === to || from < 0 || to < 0 || from >= d.items.length || to >= d.items.length) return d;
    const arr = [...d.items];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    return { ...d, items: arr };
  });
  const addService = (sid) => {
    if (!sid) return;
    const s = services.find((x) => x.id === sid);
    if (!s) return;
    addItem({
      service_id: s.id, name: s.name, description: s.description, qty: s.default_qty || 1,
      unit_price: s.unit_price || 0, vat_rate: s.vat_rate || 0,
      is_recurring: s.type === "recurring", billing_cycle: s.billing_cycle || "month",
    });
  };
  const updateItem = (i, patch) => setData((d) => ({ ...d, items: d.items.map((it, k) => k === i ? { ...it, ...patch } : it) }));
  const removeItem = (i) => setData((d) => ({ ...d, items: d.items.filter((_, k) => k !== i) }));
  const addObjective = () => setData((d) => ({ ...d, project: { ...d.project, objectives: [...(d.project.objectives || []), ""] } }));
  const updateObj = (i, v) => setData((d) => ({ ...d, project: { ...d.project, objectives: d.project.objectives.map((o, k) => k === i ? v : o) } }));
  const removeObj = (i) => setData((d) => ({ ...d, project: { ...d.project, objectives: d.project.objectives.filter((_, k) => k !== i) } }));

  const save = async () => {
    if (!data.client.name || !data.client.email) return toast.error("Client : nom + email requis");
    if (!data.project.title) return toast.error("Titre du projet requis");
    const payload = {
      ...data,
      probability: typeof data.probability === "number" ? data.probability : parseInt(data.probability) || 0,
      project: { ...data.project, objectives: (data.project.objectives || []).filter(Boolean) },
      items: data.items.map((it) => ({
        ...it,
        qty: parseFloat(it.qty) || 0,
        unit_price: parseFloat(it.unit_price) || 0,
        vat_rate: parseFloat(it.vat_rate) || 0,
      })),
      // Normalize discount: drop if kind is null or value is 0
      discount_kind: (data.discount_kind && parseFloat(data.discount_value) > 0) ? data.discount_kind : null,
      discount_value: (data.discount_kind && parseFloat(data.discount_value) > 0) ? parseFloat(data.discount_value) : null,
      discount_label: (data.discount_kind && parseFloat(data.discount_value) > 0) ? (data.discount_label || null) : null,
    };
    try {
      if (isNew) {
        const r = await api.post("/admin/proposals", { ...payload, send_welcome_email: sendWelcomeEmail });
        const w = r.data.welcome_email;
        if (w) {
          if (w.sent) toast.success(`Devis ${r.data.ref} créé · email de bienvenue envoyé ✉️`);
          else toast.success(`Devis ${r.data.ref} créé — Email de bienvenue : ${w.error || "non envoyé"} (lien copiable : ${w.token ? "OK" : "indisponible"})`);
        } else {
          toast.success(`Devis ${r.data.ref} créé`);
        }
        setPropId(r.data.id); setPropRef(r.data.ref); setToken(r.data.public_token);
        nav(`/admin/devis/${r.data.id}`, { replace: true });
      } else {
        const r = await api.patch(`/admin/proposals/${id}`, payload);
        toast.success("Devis enregistré");
        setPropRef(r.data.ref);
        // Auto-refresh PDF preview when split-screen is open
        if (showPreview) setTimeout(() => refreshPreview(), 400);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const downloadPdf = () => {
    const url = `${window.location.origin}/api/admin/proposals/${propId}/pdf`;
    const tok = localStorage.getItem("besson_admin_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); window.open(u, "_blank"); });
  };

  const addExchange = async () => {
    if (!propId) { toast.error("Enregistrez d'abord le devis"); return; }
    if (!newExchange.note?.trim() && newExchange.kind !== "appel") {
      // Allow empty note for quick "appel" logging only
    }
    setExBusy(true);
    try {
      await api.post(`/admin/proposals/${propId}/exchanges`, { kind: newExchange.kind, note: newExchange.note });
      setNewExchange({ kind: newExchange.kind, note: "" });
      loadExchanges();
      toast.success("Échange enregistré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setExBusy(false); }
  };

  const removeExchange = async (eid) => {
    if (!window.confirm("Supprimer cet échange ?")) return;
    try { await api.delete(`/admin/proposals/${propId}/exchanges/${eid}`); loadExchanges(); }
    catch { toast.error("Erreur"); }
  };

  const probabilityColor = (() => {
    const p = data.probability ?? 0;
    if (p >= 70) return "#16a34a";
    if (p >= 40) return "#06B6D4";
    if (p >= 20) return "#FFDCA8";
    return "#94a3b8";
  })();

  // Live payment schedule preview — mirrors backend logic
  const schedule = useMemo(() => {
    const total = totals.total_ttc;
    if (total <= 0) return { kind: data.payment_terms, installments: [], summary: "—" };
    const today = new Date();
    const addMonths = (d, n) => {
      const r = new Date(d);
      r.setMonth(r.getMonth() + n);
      return r;
    };
    const fmtDate = (d) => d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    if (data.payment_terms === "100_upfront") {
      return { kind: "100_upfront", summary: `${total.toFixed(2)} € à la signature`, installments: [{ index: 1, label: "Paiement intégral", amount: total, due_at: fmtDate(today), remaining: 0 }] };
    }
    if (data.payment_terms === "50_50") {
      const deposit = Math.round(total * 50) / 100;
      const balance = Math.round((total - deposit) * 100) / 100;
      return {
        kind: "50_50",
        summary: `${deposit.toFixed(2)} € à la signature, puis ${balance.toFixed(2)} € à la livraison`,
        installments: [
          { index: 1, label: "Acompte 50%", amount: deposit, due_at: fmtDate(today), remaining: balance },
          { index: 2, label: "Solde 50%", amount: balance, due_at: fmtDate(addMonths(today, 1)), remaining: 0 },
        ],
      };
    }
    // installment
    const n = Math.max(2, Math.min(24, parseInt(data.installment_count) || 6));
    const per = Math.round((total / n) * 100) / 100;
    const items = [];
    let running = 0;
    for (let i = 1; i <= n; i++) {
      const amount = i < n ? per : Math.round((total - running) * 100) / 100;
      running = Math.round((running + amount) * 100) / 100;
      items.push({
        index: i,
        label: `Mensualité ${i}/${n}`,
        amount,
        due_at: fmtDate(addMonths(today, i - 1)),
        remaining: Math.round((total - running) * 100) / 100,
      });
    }
    return { kind: "installment", summary: `${n} mensualités de ${per.toFixed(2)} € (sur ${total.toFixed(2)} €)`, installments: items };
  }, [data.payment_terms, data.installment_count, totals.total_ttc]);

  // Live PDF preview — visible by default on xl screens
  const [showPreview, setShowPreview] = useState(typeof window !== "undefined" ? window.innerWidth >= 1280 : true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const refreshPreview = useCallback(async () => {
    if (!propId || isNew) return;
    setPdfBusy(true);
    try {
      const url = `${window.location.origin}/api/admin/proposals/${propId}/pdf?ts=${Date.now()}`;
      const tok = localStorage.getItem("besson_admin_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!resp.ok) return;
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl((old) => { if (old) URL.revokeObjectURL(old); return blobUrl; });
    } catch {} finally { setPdfBusy(false); }
  }, [propId, isNew]);
  useEffect(() => { if (showPreview && propId) refreshPreview(); /* eslint-disable-next-line */ }, [propId, showPreview]);

  if (!loaded) return <div className="text-[#64748B]">Chargement...</div>;

  return (
    <div data-testid="proposal-edit" className={`grid gap-4 ${showPreview && !isNew ? "xl:grid-cols-2" : ""}`}>
      <div className="space-y-6 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">{isNew ? "Nouveau devis" : `Devis ${propRef}`}</h1>
          <p className="text-sm text-[#64748B] mt-1">Brief client + lignes + signature & paiement plus tard.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} data-testid="prop-save" className="bg-[#0A1128] text-white"><Save size={14} className="mr-1" />Enregistrer</Button>
          {!isNew && propId && <Button onClick={downloadPdf} data-testid="prop-pdf" variant="outline"><FileDown size={14} className="mr-1" />PDF</Button>}
          {!isNew && propId && (
            <Button onClick={() => { setShowPreview((p) => !p); }} data-testid="prop-toggle-preview" variant="outline" className="hidden xl:inline-flex">
              {showPreview ? <><Minimize2 size={14} className="mr-1" />Masquer aperçu</> : <><Maximize2 size={14} className="mr-1" />Aperçu PDF</>}
            </Button>
          )}
          {!isNew && propId && <Button onClick={() => setSendModal(true)} data-testid="prop-send" className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9]"><Send size={14} className="mr-1" />Envoyer au client</Button>}
        </div>
      </div>

      {/* Client + Project blocks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/8 rounded-2xl p-5">
          <h3 className="display font-bold text-[#0A1128] mb-3">Client</h3>
          <ClientAutocomplete
            value={data.client.name}
            onSelect={(c) => {
              const full = (c.name || "").trim();
              const parts = full.split(/\s+/);
              const fn = c.first_name || parts[0] || "";
              const ln = c.last_name || (parts.length > 1 ? parts.slice(1).join(" ") : "");
              setData({ ...data, client: {
                ...data.client,
                first_name: fn,
                last_name: ln,
                name: `${fn} ${ln}`.trim() || full,
                email: c.email || data.client.email,
                company: c.company || data.client.company,
                phone: c.phone || data.client.phone,
                address: c.address || data.client.address,
              } });
            }}
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label>Prénom *</Label>
              <Input
                data-testid="prop-client-first-name"
                value={data.client.first_name || ""}
                onChange={(e) => {
                  const fn = e.target.value;
                  const ln = data.client.last_name || "";
                  setData({ ...data, client: { ...data.client, first_name: fn, name: `${fn} ${ln}`.trim() } });
                }}
                placeholder="Marie"
              />
            </div>
            <div>
              <Label>Nom *</Label>
              <Input
                data-testid="prop-client-last-name"
                value={data.client.last_name || ""}
                onChange={(e) => {
                  const ln = e.target.value;
                  const fn = data.client.first_name || "";
                  setData({ ...data, client: { ...data.client, last_name: ln, name: `${fn} ${ln}`.trim() } });
                }}
                placeholder="Démo"
              />
            </div>
            <div><Label>Email *</Label><Input data-testid="prop-client-email" type="email" value={data.client.email} onChange={(e) => setData({ ...data, client: { ...data.client, email: e.target.value } })} /></div>
            <div><Label>Téléphone</Label><Input value={data.client.phone || ""} onChange={(e) => setData({ ...data, client: { ...data.client, phone: e.target.value } })} /></div>
            <div className="col-span-2"><Label>Entreprise</Label><Input value={data.client.company || ""} onChange={(e) => setData({ ...data, client: { ...data.client, company: e.target.value } })} /></div>
            <div className="col-span-2"><Label>Adresse</Label><Input value={data.client.address || ""} onChange={(e) => setData({ ...data, client: { ...data.client, address: e.target.value } })} /></div>
          </div>
          {isNew && (
            <label className="mt-4 flex items-start gap-2.5 p-3 rounded-xl bg-[#06B6D4]/8 border border-[#06B6D4]/30 cursor-pointer" data-testid="prop-send-welcome-wrap">
              <input
                type="checkbox"
                checked={sendWelcomeEmail}
                onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                data-testid="prop-send-welcome"
                className="mt-0.5 w-4 h-4 accent-[#06B6D4] cursor-pointer"
              />
              <span className="text-xs leading-snug text-[#0A1128]">
                <span className="font-bold">Envoyer un email de bienvenue au client</span> — un lien d'activation (valide 7 jours) sera envoyé à l'email saisi, pour qu'il puisse définir son mot de passe et accéder à son Espace Entreprise.
                <span className="block text-[10px] text-[#64748B] mt-0.5 italic">Ignoré si le client a déjà un compte.</span>
              </span>
            </label>
          )}
        </div>
        <div className="bg-white border border-black/8 rounded-2xl p-5">
          <h3 className="display font-bold text-[#0A1128] mb-3">Projet</h3>
          <div className="space-y-3">
            <div><Label>Titre *</Label><Input data-testid="prop-project-title" value={data.project.title} onChange={(e) => setData({ ...data, project: { ...data.project, title: e.target.value } })} /></div>
            <div><Label>Brief / contexte</Label><Textarea rows={3} value={data.project.brief || ""} onChange={(e) => setData({ ...data, project: { ...data.project, brief: e.target.value } })} /></div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label>Objectifs</Label><button onClick={addObjective} className="text-xs text-[#06B6D4] font-bold">+ ajouter</button></div>
              <div className="space-y-2">
                {(data.project.objectives || []).map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={o} onChange={(e) => updateObj(i, e.target.value)} placeholder={`Objectif ${i + 1}`} />
                    <button onClick={() => removeObj(i)} className="text-[#94a3b8] hover:text-red-500"><X size={14} /></button>
                  </div>
                ))}
                {(data.project.objectives || []).length === 0 && <div className="text-xs text-[#64748B]">Aucun objectif</div>}
              </div>
            </div>
            <div><Label>Délai / échéance</Label><Input placeholder="ex: Livraison T3 2026" value={data.project.deadline || ""} onChange={(e) => setData({ ...data, project: { ...data.project, deadline: e.target.value } })} /></div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white border border-black/8 rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="display font-bold text-[#0A1128]">Lignes du devis</h3>
          <div className="flex gap-2 flex-wrap">
            <select onChange={(e) => { addService(e.target.value); e.target.value = ""; }} className="border border-black/10 rounded-md px-3 py-2 text-sm">
              <option value="">+ Ajouter depuis catalogue</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.unit_price}€</option>)}
            </select>
            <Button data-testid="prop-add-custom" onClick={() => addItem()} variant="outline"><Plus size={14} className="mr-1" />Ligne libre</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-black/8 text-[#64748B]"><th className="py-2 pr-3">Désignation</th><th className="py-2 pr-2 w-16">Qté</th><th className="py-2 pr-2 w-24">PU HT €</th><th className="py-2 pr-2 w-32">Remise ligne</th><th className="py-2 pr-2 w-16">TVA %</th><th className="py-2 pr-2 w-28">Total HT</th><th className="py-2 pr-2 w-28">Type</th><th></th></tr></thead>
            <tbody>
              {data.items.map((it, i) => {
                const qty = parseFloat(it.qty) || 0;
                const pu = parseFloat(it.unit_price) || 0;
                const gross = qty * pu;
                const dk = it.discount_kind;
                const dv = parseFloat(it.discount_value) || 0;
                let lineDisc = 0;
                if (dk === "percent" && dv > 0) lineDisc = Math.round(gross * Math.min(100, dv) / 100 * 100) / 100;
                else if (dk === "amount" && dv > 0) lineDisc = Math.round(Math.min(dv, gross) * 100) / 100;
                const netHt = gross - lineDisc;
                return (
                  <tr key={i} className="border-b border-black/5 align-top" draggable
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                      onDrop={(e) => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData("text/plain"), 10); if (!isNaN(from)) moveItem(from, i); }}
                      data-testid={`prop-item-row-${i}`}>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="cursor-grab text-[#94a3b8] hover:text-[#06B6D4]" title="Glisser pour réorganiser"><GripVertical size={14} /></span>
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#94a3b8] tabular-nums">#{i + 1}</span>
                        <PenSquare size={11} className="text-[#06B6D4] ml-1" />
                      </div>
                      <Input value={it.name} placeholder="Nom" onChange={(e) => updateItem(i, { name: e.target.value })} />
                      <Textarea className="mt-1" rows={1} value={it.description || ""} placeholder="Description (optionnel)" onChange={(e) => updateItem(i, { description: e.target.value })} />
                      <div className="mt-1.5 flex flex-wrap gap-2 text-[10px]">
                        <button type="button" onClick={() => duplicateItem(i)} data-testid={`prop-item-${i}-duplicate`} className="inline-flex items-center gap-1 text-[#06B6D4] hover:underline font-bold"><Copy size={10} />Dupliquer</button>
                        <button type="button" onClick={() => saveItemToCatalog(i)} data-testid={`prop-item-${i}-save-catalog`} className="inline-flex items-center gap-1 text-[#0A1128] hover:underline font-bold"><BookOpen size={10} />Enregistrer au catalogue</button>
                        {i > 0 && <button type="button" onClick={() => moveItem(i, i - 1)} className="inline-flex items-center gap-1 text-[#64748B] hover:text-[#0A1128] font-bold" title="Monter">↑</button>}
                        {i < data.items.length - 1 && <button type="button" onClick={() => moveItem(i, i + 1)} className="inline-flex items-center gap-1 text-[#64748B] hover:text-[#0A1128] font-bold" title="Descendre">↓</button>}
                      </div>
                    </td>
                    <td className="py-2 pr-2"><Input type="number" step="0.1" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} /></td>
                    <td className="py-2 pr-2"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} /></td>
                    <td className="py-2 pr-2">
                      <div className="flex gap-1">
                        <select
                          className="border border-black/10 rounded-md px-1.5 py-2 text-xs font-bold bg-white"
                          value={it.discount_kind || ""}
                          onChange={(e) => updateItem(i, { discount_kind: e.target.value || null })}
                          data-testid={`prop-item-${i}-disc-kind`}
                          aria-label="Type de remise (% ou €)"
                        >
                          <option value="">—</option>
                          <option value="percent">%</option>
                          <option value="amount">€</option>
                        </select>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max={it.discount_kind === "percent" ? 100 : undefined}
                          value={it.discount_value || ""}
                          placeholder="0"
                          disabled={!it.discount_kind}
                          onChange={(e) => updateItem(i, { discount_value: e.target.value })}
                          data-testid={`prop-item-${i}-disc-value`}
                          className="text-xs"
                        />
                      </div>
                      {lineDisc > 0 && <div className="text-[10px] text-[#92400E] font-bold mt-0.5">-{lineDisc.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € HT</div>}
                    </td>
                    <td className="py-2 pr-2"><Input type="number" value={it.vat_rate} onChange={(e) => updateItem(i, { vat_rate: e.target.value })} /></td>
                    <td className="py-2 pr-2 font-semibold">{netHt.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</td>
                    <td className="py-2 pr-2">
                      <select className="border border-black/10 rounded-md px-2 py-2 text-xs w-full" value={it.is_recurring ? `r-${it.billing_cycle || "month"}` : "one_off"} onChange={(e) => {
                        const v = e.target.value;
                        if (v === "one_off") updateItem(i, { is_recurring: false });
                        else updateItem(i, { is_recurring: true, billing_cycle: v === "r-year" ? "year" : "month" });
                      }}>
                        <option value="one_off">Ponctuel</option>
                        <option value="r-month">Récurrent /Mois</option>
                        <option value="r-year">Récurrent /An</option>
                      </select>
                    </td>
                    <td><button onClick={() => removeItem(i)} className="text-[#94a3b8] hover:text-red-500"><Trash2 size={14} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.items.length === 0 && <div className="text-center text-[#64748B] py-6">Aucune ligne. Ajoutez depuis votre catalogue ou créez une ligne libre.</div>}
        </div>

        {/* Totals */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label>Modalité de paiement</Label>
              <select
                data-testid="prop-payment-terms"
                className="w-full border border-black/10 rounded-md px-3 py-2 text-sm mt-1"
                value={data.payment_terms}
                onChange={(e) => setData({ ...data, payment_terms: e.target.value })}
              >
                <option value="50_50">50% acompte + 50% à la livraison</option>
                <option value="100_upfront">100% à la signature</option>
                <option value="installment">Échelonnement mensuel (N mensualités)</option>
              </select>
            </div>
            {data.payment_terms === "installment" && (
              <div className="grid grid-cols-2 gap-3 bg-[#06B6D4]/5 border border-[#06B6D4]/20 rounded-xl p-3">
                <div>
                  <Label>Nombre de mensualités</Label>
                  <input
                    type="number"
                    min="2"
                    max="24"
                    data-testid="prop-installment-count"
                    value={data.installment_count || 6}
                    onChange={(e) => setData({ ...data, installment_count: parseInt(e.target.value) || 0 })}
                    className="w-full border border-black/10 rounded-md px-3 py-2 text-sm mt-1"
                  />
                  <p className="text-[10px] text-[#64748B] mt-1">entre 2 et 24</p>
                </div>
                <div>
                  <Label>1<sup>er</sup> prélèvement (optionnel)</Label>
                  <input
                    type="date"
                    data-testid="prop-installment-start"
                    value={data.installment_start || ""}
                    onChange={(e) => setData({ ...data, installment_start: e.target.value })}
                    className="w-full border border-black/10 rounded-md px-3 py-2 text-sm mt-1"
                  />
                  <p className="text-[10px] text-[#64748B] mt-1">défaut : date de signature</p>
                </div>
              </div>
            )}
            {(data.payment_terms === "installment" || data.payment_terms === "50_50") && (
              <div className="bg-[#0A1128]/[0.03] border border-[#0A1128]/10 rounded-xl p-3">
                <Label>Mode de paiement des échéances ultérieures</Label>
                <select
                  data-testid="prop-payment-mode"
                  className="w-full border border-black/10 rounded-md px-3 py-2 text-sm mt-1"
                  value={data.payment_mode || "manual_link"}
                  onChange={(e) => setData({ ...data, payment_mode: e.target.value })}
                >
                  <option value="manual_link">📧 Lien Stripe envoyé par email à chaque échéance (paiement manuel)</option>
                  <option value="auto_card">💳 Prélèvement automatique sur la carte du 1er paiement</option>
                </select>
                <p className="text-[10px] text-[#64748B] mt-2 leading-relaxed">
                  {data.payment_mode === "auto_card" ? (
                    <>Le 1<sup>er</sup> paiement enregistre la carte du client (off-session). Les échéances suivantes sont prélevées automatiquement à la date d'échéance. En cas d'échec, un email avec lien de paiement manuel est envoyé.</>
                  ) : (
                    <>Une facture + un lien de paiement Stripe sont envoyés au client par email à chaque date d'échéance. Le client règle quand il le souhaite.</>
                  )}
                </p>
              </div>
            )}
          </div>
          <div className="bg-[#FAFAFA] rounded-xl border border-black/8 p-4">
            {/* Commercial discount controls */}
            <div className="mb-3 pb-3 border-b border-black/8" data-testid="prop-discount-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[#64748B]">Remise commerciale</span>
                {data.discount_kind && parseFloat(data.discount_value) > 0 && (
                  <button
                    type="button"
                    onClick={() => setData({ ...data, discount_kind: null, discount_value: 0, discount_label: "" })}
                    className="text-[10px] text-red-600 hover:underline font-bold"
                    data-testid="prop-discount-clear"
                  >Retirer</button>
                )}
              </div>
              <div className="flex items-stretch gap-2">
                <div className="inline-flex rounded-md border border-black/10 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setData({ ...data, discount_kind: "percent" })}
                    className={`px-3 py-1.5 text-xs font-bold ${data.discount_kind === "percent" ? "bg-[#0A1128] text-white" : "bg-white text-[#64748B] hover:text-[#0A1128]"}`}
                    data-testid="prop-discount-kind-percent"
                  >%</button>
                  <button
                    type="button"
                    onClick={() => setData({ ...data, discount_kind: "amount" })}
                    className={`px-3 py-1.5 text-xs font-bold border-l border-black/10 ${data.discount_kind === "amount" ? "bg-[#0A1128] text-white" : "bg-white text-[#64748B] hover:text-[#0A1128]"}`}
                    data-testid="prop-discount-kind-amount"
                  >€</button>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={data.discount_kind === "percent" ? 100 : undefined}
                  value={data.discount_value || ""}
                  onChange={(e) => setData({ ...data, discount_value: e.target.value, discount_kind: data.discount_kind || "percent" })}
                  placeholder={data.discount_kind === "amount" ? "Montant HT" : "%"}
                  className="flex-1 border border-black/10 rounded-md px-2 py-1.5 text-sm"
                  data-testid="prop-discount-value"
                />
              </div>
              <input
                type="text"
                value={data.discount_label || ""}
                onChange={(e) => setData({ ...data, discount_label: e.target.value })}
                placeholder="Motif (ex. : geste commercial, fidélité…)"
                className="w-full mt-2 border border-black/10 rounded-md px-2 py-1.5 text-xs"
                data-testid="prop-discount-label"
              />
            </div>

            <div className="flex justify-between text-sm"><span className="text-[#64748B]">Total HT</span><span className="font-semibold">{totals.subtotal_ht.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
            {totals.discount_amount_ht > 0 && (
              <>
                <div className="flex justify-between text-sm text-[#92400E]" data-testid="prop-totals-discount">
                  <span>Remise{data.discount_kind === "percent" && data.discount_value ? ` (${parseFloat(data.discount_value)}%)` : ""}</span>
                  <span className="font-semibold">-{totals.discount_amount_ht.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="flex justify-between text-sm border-t border-dashed border-[#FFDCA8] pt-1 mt-1"><span className="text-[#64748B]">Total HT remisé</span><span className="font-semibold">{totals.subtotal_after_discount_ht.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
              </>
            )}
            <div className="flex justify-between text-sm"><span className="text-[#64748B]">TVA</span><span className="font-semibold">{totals.vat.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
            <div className="flex justify-between text-lg mt-2 pt-2 border-t border-black/8" data-testid="prop-total-ttc"><span className="display font-extrabold">Total TTC</span><span className="display font-extrabold text-[#0A1128]">{totals.total_ttc.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
            {totals.recurring_monthly_ttc > 0 && <div className="text-xs text-[#06B6D4] mt-2 font-bold">+ {totals.recurring_monthly_ttc.toLocaleString("fr-FR")} € TTC /Mois (récurrent)</div>}
            {totals.recurring_yearly_ttc > 0 && <div className="text-xs text-[#06B6D4] mt-1 font-bold">+ {totals.recurring_yearly_ttc.toLocaleString("fr-FR")} € TTC /An (récurrent)</div>}
          </div>
        </div>

        {/* Live payment schedule */}
        {totals.total_ttc > 0 && schedule.installments.length > 0 && (
          <div data-testid="prop-schedule" className="mt-5 bg-white border border-[#06B6D4]/30 rounded-2xl p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={16} className="text-[#06B6D4]" />
              <h4 className="display font-bold text-[#0A1128]">Échéancier du client</h4>
              <span className="ml-auto text-xs font-bold text-[#06B6D4] bg-[#06B6D4]/10 px-2.5 py-1 rounded-full">{schedule.summary}</span>
            </div>
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#64748B] border-b border-black/5">
                    <th className="text-left py-2">Échéance</th>
                    <th className="text-left py-2">Date</th>
                    <th className="text-right py-2">Montant</th>
                    <th className="text-right py-2">Solde restant</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.installments.map((it) => (
                    <tr key={it.index} className="border-b border-black/5 last:border-0">
                      <td className="py-2.5 font-bold text-[#0A1128]">{it.label}</td>
                      <td className="py-2.5 text-[#64748B]">{it.due_at}</td>
                      <td className="py-2.5 text-right font-extrabold text-[#0A1128] tabular-nums">{it.amount.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</td>
                      <td className="py-2.5 text-right text-[#64748B] tabular-nums">
                        {it.remaining > 0
                          ? `${it.remaining.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`
                          : <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#A7F3D0] text-[#065F46]">soldé</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-black/8 rounded-2xl p-5">
        <Label>Notes / mentions complémentaires</Label>
        <Textarea rows={3} value={data.notes || ""} onChange={(e) => setData({ ...data, notes: e.target.value })} />
      </div>

      {/* Probability + status — opportunity tracking */}
      {!isNew && (
        <div data-testid="prop-opportunity-block" className="bg-white border border-black/8 rounded-2xl p-5">
          <h3 className="display font-bold text-[#0A1128] mb-4 flex items-center gap-2"><Activity size={16} className="text-[#06B6D4]" /> Suivi opportunité</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Statut</Label>
              <select
                data-testid="prop-status"
                value={data.status}
                onChange={(e) => setData({ ...data, status: e.target.value })}
                className="w-full border border-black/10 rounded-md px-3 py-2 text-sm mt-1"
              >
                <option value="brouillon">Brouillon</option>
                <option value="envoye">Envoyé</option>
                <option value="negociation">Négociation</option>
                <option value="signe">Signé</option>
                <option value="paye_acompte">Acompte payé</option>
                <option value="en_cours">En cours</option>
                <option value="livre">Livré</option>
                <option value="paye_solde">Soldé</option>
                <option value="cloture">Clôturé</option>
                <option value="refuse">Refusé</option>
                <option value="expire">Expiré</option>
              </select>
              <p className="text-[11px] text-[#64748B] mt-1">Le changement de statut ajuste la probabilité automatiquement (modifiable).</p>
            </div>
            <div>
              <Label>Probabilité de signature : <span className="font-bold" style={{ color: probabilityColor }}>{data.probability ?? 0}%</span></Label>
              <input
                data-testid="prop-probability"
                type="range"
                min="0"
                max="100"
                step="5"
                value={data.probability ?? 0}
                onChange={(e) => setData({ ...data, probability: parseInt(e.target.value) })}
                className="w-full mt-2 accent-[#06B6D4]"
              />
              <div className="flex justify-between text-[10px] text-[#64748B] mt-1"><span>0%</span><span>25</span><span>50</span><span>75</span><span>100%</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Exchanges timeline */}
      {!isNew && propId && (
        <div data-testid="prop-exchanges-block" className="bg-white border border-black/8 rounded-2xl p-5">
          <h3 className="display font-bold text-[#0A1128] mb-4 flex items-center gap-2"><MessageSquare size={16} className="text-[#06B6D4]" /> Échanges & relances</h3>

          {/* Quick add */}
          <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-2 items-start">
              <select
                data-testid="exchange-kind"
                value={newExchange.kind}
                onChange={(e) => setNewExchange({ ...newExchange, kind: e.target.value })}
                className="border border-black/10 rounded-md px-3 py-2 text-sm bg-white"
              >
                {EXCHANGE_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
              <Textarea
                data-testid="exchange-note"
                rows={1}
                placeholder="Commentaire rapide (ex: « rappelé sans réponse, relance vendredi »)"
                value={newExchange.note}
                onChange={(e) => setNewExchange({ ...newExchange, note: e.target.value })}
              />
              <Button data-testid="exchange-add" onClick={addExchange} disabled={exBusy} className="bg-[#0A1128] hover:bg-[#14213d] text-white whitespace-nowrap">
                <Plus size={14} className="mr-1" />Ajouter
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            {exchanges.length === 0 && (
              <div className="text-sm text-[#64748B] text-center py-6">Aucun échange enregistré. Loggez vos appels, SMS et RDV pour garder l'historique.</div>
            )}
            {exchanges.map((ex) => {
              const kind = EXCHANGE_KINDS.find((k) => k.v === ex.kind) || EXCHANGE_KINDS[EXCHANGE_KINDS.length - 1];
              const Icon = kind.icon;
              const date = ex.happened_at ? new Date(ex.happened_at) : null;
              return (
                <div key={ex.id} data-testid={`exchange-${ex.id}`} className="flex items-start gap-3 p-3 rounded-xl border border-black/5 hover:bg-[#FAFAFA] transition-colors">
                  <div className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full" style={{ background: `${kind.color}22`, color: kind.color, border: `1px solid ${kind.color}40` }}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[#0A1128] text-sm">{kind.l}</span>
                      <span className="text-[11px] text-[#64748B] tabular-nums">{date ? date.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                      <span className="text-[11px] text-[#94a3b8]">· {ex.author_name}</span>
                    </div>
                    {ex.note && <p className="text-sm text-[#475569] mt-1 whitespace-pre-wrap">{ex.note}</p>}
                  </div>
                  <button onClick={() => removeExchange(ex.id)} className="text-[#cbd5e1] hover:text-red-500 transition-colors" title="Supprimer">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {token && (
        <div className="bg-[#0A1128] text-white rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-[#67E8F9] mb-1">Lien client</div>
          <div className="font-mono text-sm break-all">{window.location.origin}/devis/{token}</div>
        </div>
      )}

      {sendModal && propId && (
        <ProposalSendModal
          proposal={{ id: propId, ref: propRef, client: data.client, project: data.project }}
          onClose={() => setSendModal(false)}
          onSent={() => toast.success("Devis envoyé au client")}
        />
      )}
      </div>

      {/* Right pane — live PDF preview (only when toggled + propId exists, xl+ screens) */}
      {showPreview && !isNew && propId && (
        <aside data-testid="prop-pdf-preview" className="hidden xl:flex flex-col sticky top-24 self-start h-[calc(100vh-7rem)] bg-white border border-black/8 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-black/5 bg-[#FAFAFA]">
            <FileText size={14} className="text-[#06B6D4]" />
            <span className="text-xs font-bold uppercase tracking-wider text-[#0A1128]">Aperçu PDF</span>
            <span className="text-[10px] text-[#64748B]">— se met à jour après chaque enregistrement</span>
            <Button
              size="sm"
              variant="outline"
              onClick={refreshPreview}
              data-testid="prop-pdf-refresh"
              className="ml-auto h-7 px-2 text-[11px]"
              disabled={pdfBusy}
            >
              <RefreshCw size={11} className={`mr-1 ${pdfBusy ? "animate-spin" : ""}`} /> {pdfBusy ? "MAJ…" : "Actualiser"}
            </Button>
          </div>
          {pdfBlobUrl ? (
            <object
              key={pdfBlobUrl}
              data={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              type="application/pdf"
              className="flex-1 w-full bg-white"
              aria-label="Aperçu PDF"
            >
              <embed
                src={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
                type="application/pdf"
                className="w-full h-full"
              />
            </object>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-[#64748B]">
              {pdfBusy ? "Chargement du PDF…" : "Cliquez « Actualiser » pour générer l'aperçu."}
            </div>
          )}
        </aside>
      )}
    </div>
  );
}


function ClientAutocomplete({ value, onSelect }) {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/entrepreneurs").then((r) => {
      const list = [];
      (r.data || []).forEach((e) => {
        (e.companies || []).forEach((c) => list.push({
          name: e.full_name || c.name || e.email,
          email: c.email || e.email,
          company: c.name,
          phone: c.phone || e.phone,
          address: c.address || "",
        }));
      });
      setClients(list);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!q) return clients.slice(0, 6);
    const s = q.toLowerCase();
    return clients.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.company || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s)).slice(0, 8);
  }, [clients, q]);

  return (
    <div className="relative">
      <Label className="text-xs">🔍 Rechercher un client existant ou créer un nouveau ci-dessous</Label>
      <Input
        data-testid="prop-client-autocomplete"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Tapez le nom, l'email ou l'entreprise…"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-black/10 rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {filtered.map((c, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onSelect(c); setQ(""); setOpen(false); toast.success(`Client "${c.name}" sélectionné`); }}
              className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] text-sm border-b border-black/5 last:border-0"
            >
              <div className="font-medium text-[#0A1128]">{c.name}</div>
              <div className="text-xs text-[#64748B]">{c.company} · {c.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
